import React, { useEffect, useState } from "react";
import {
  Modal,
  TextInput,
  Textarea,
  Select,
  SegmentedControl,
  Button,
  Group,
  Stack,
  Grid,
  Text,
} from "@mantine/core";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { getApiEndpoint } from "../../config/config";
import { uniqueOptions } from "./AddRepFromLeadModal";

// The stages a new lead can start in (POST /v2/recruiting/leads `status`)
const START_STAGES = [
  { value: "uncontacted", label: "Uncontacted" },
  { value: "contacted", label: "Contacted" },
  { value: "offer_sent", label: "Offer Sent" },
];

// Value for "assign to me" in the manager picker; the API defaults to the caller
const ME = "me";

const EMPTY_FORM = {
  name: "",
  status: "contacted",
  recruitingSourceId: null,
  managerUserId: ME,
  phone: "",
  email: "",
  instagram: "",
  linkedin: "",
  notes: "",
};

/// The Leads tab's Add Lead, the web version of iOS's AddLeadSheet:
/// POST /v2/recruiting/leads. `managers` is the pipeline's `manager_list`.
function NewLeadModal({ opened, managers = [], onClose, onAdded }) {
  const { getVerifiedJWT } = useAuth();
  const { showSuccess } = useToast();

  const [form, setForm] = useState(EMPTY_FORM);
  const [recruitingSources, setRecruitingSources] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  useEffect(() => {
    if (!opened) return;
    setForm(EMPTY_FORM);
    setError(null);

    let cancelled = false;
    const loadSources = async () => {
      try {
        const jwt = await getVerifiedJWT();
        if (!jwt) return;
        const response = await fetch(
          getApiEndpoint("/v2/company/recruiting-sources"),
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${jwt}`,
            },
          }
        );
        if (!response.ok) return;
        const sources = (await response.json()).data || [];
        if (!cancelled) setRecruitingSources(sources);
      } catch {
        // The picker stays empty and the form can't be saved
      }
    };
    loadSources();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened]);

  const trimmed = value => value.trim() || undefined;
  const emailOk =
    !form.email.trim() || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim());
  const canSave =
    form.name.trim() && form.recruitingSourceId && emailOk && !saving;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    setError(null);
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) throw new Error("Authentication failed");
      const body = {
        name: form.name.trim(),
        status: form.status,
        recruiting_source_id: Number(form.recruitingSourceId),
        ...(form.managerUserId !== ME && {
          manager_user_id: Number(form.managerUserId),
        }),
        phone: trimmed(form.phone.replace(/\D/g, "")),
        email: trimmed(form.email),
        instagram: trimmed(form.instagram),
        linkedin: trimmed(form.linkedin),
        notes: trimmed(form.notes),
      };
      const response = await fetch(getApiEndpoint("/v2/recruiting/leads"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        let message = "Failed to add lead";
        try {
          const data = await response.json();
          const raw = data.message ?? data.error;
          if (raw) message = Array.isArray(raw) ? raw.join(", ") : raw;
        } catch {
          // keep the default
        }
        throw new Error(message);
      }
      showSuccess("Lead added");
      onAdded?.();
      onClose();
    } catch (e) {
      setError(e.message || "Failed to add lead");
    } finally {
      setSaving(false);
    }
  };

  const managerOptions = uniqueOptions([
    { value: ME, label: "Me" },
    ...managers.map(m => ({
      value: String(m.id),
      label: [m.first_name, m.last_name].filter(Boolean).join(" "),
    })),
  ]);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Add Lead"
      centered
      size="lg"
    >
      <Stack gap="md">
        <TextInput
          label="Name"
          placeholder="Full name"
          value={form.name}
          onChange={e => set("name", e.currentTarget.value)}
          maxLength={50}
          required
          disabled={saving}
          data-autofocus
        />

        <div>
          <Text size="sm" fw={500} mb={4}>
            Stage
          </Text>
          <SegmentedControl
            fullWidth
            data={START_STAGES}
            value={form.status}
            onChange={value => set("status", value)}
            disabled={saving}
          />
        </div>

        <Grid>
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <Select
              label="Recruiting source"
              placeholder="Select source"
              data={uniqueOptions(
                recruitingSources.map(s => ({
                  value: String(s.id),
                  label: s.source_name,
                }))
              )}
              value={form.recruitingSourceId}
              onChange={value => set("recruitingSourceId", value)}
              searchable
              required
              disabled={saving}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <Select
              label="Pipeline"
              data={managerOptions}
              value={form.managerUserId}
              onChange={value => set("managerUserId", value || ME)}
              searchable
              allowDeselect={false}
              disabled={saving}
            />
          </Grid.Col>
        </Grid>

        <Grid>
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <TextInput
              label="Phone"
              placeholder="(000) 000-0000"
              value={form.phone}
              onChange={e => set("phone", e.currentTarget.value)}
              disabled={saving}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <TextInput
              type="email"
              label="Email"
              placeholder="name@email.com"
              value={form.email}
              onChange={e => set("email", e.currentTarget.value)}
              error={!emailOk ? "Enter a valid email" : null}
              disabled={saving}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <TextInput
              label="Instagram"
              placeholder="@handle"
              value={form.instagram}
              onChange={e => set("instagram", e.currentTarget.value)}
              disabled={saving}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <TextInput
              label="LinkedIn"
              placeholder="Profile URL"
              value={form.linkedin}
              onChange={e => set("linkedin", e.currentTarget.value)}
              disabled={saving}
            />
          </Grid.Col>
        </Grid>

        <Textarea
          label="Notes"
          placeholder="Add a note..."
          value={form.notes}
          onChange={e => set("notes", e.currentTarget.value)}
          autosize
          minRows={2}
          disabled={saving}
        />

        {error && (
          <Text c="red" size="sm">
            {error}
          </Text>
        )}

        <Group justify="flex-end" mt="xs">
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} loading={saving} disabled={!canSave}>
            Add Lead
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

export default NewLeadModal;
